import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { Plus, Edit2, Trash2, X, FileText, Video, Link as LinkIcon, File } from 'lucide-react'
import api from '../services/api'
import Card from '../components/Card'
import LoadingSpinner from '../components/LoadingSpinner'
import { getToken, clearAuth } from '../utils/userData'

const TYPE_OPTIONS = [
  { value: 'pdf', label: 'PDF' },
  { value: 'video', label: 'Video' },
  { value: 'doc', label: 'Document' },
  { value: 'link', label: 'Link' },
]

const TYPE_META = {
  pdf:   { icon: FileText, color: 'bg-red-100 text-red-700' },
  video: { icon: Video,    color: 'bg-purple-100 text-purple-700' },
  link:  { icon: LinkIcon, color: 'bg-blue-100 text-blue-700' },
  doc:   { icon: File,     color: 'bg-yellow-100 text-yellow-700' },
}

const INITIAL_FORM = {
  title: '',
  description: '',
  resourceType: 'pdf',
  fileUrl: '',
}

const FacilitatorResourceManage = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [resources, setResources] = useState([])
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(INITIAL_FORM)
  const [resourceFile, setResourceFile] = useState(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const token = getToken()
    if (!token) {
      navigate('/')
      return
    }
    fetchResources()
  }, [navigate])

  const fetchResources = async () => {
    try {
      setLoading(true)
      const response = await api.get('/api/facilitators/resources')
      if (response.data.success) {
        setResources(response.data.data)
      }
    } catch (err) {
      if (err.response?.status === 401) {
        clearAuth()
        navigate('/')
      }
      console.error('Error fetching resources:', err)
    } finally {
      setLoading(false)
    }
  }

  const openCreateForm = () => {
    setForm(INITIAL_FORM)
    setResourceFile(null)
    setEditingId(null)
    setShowForm(true)
  }

  const openEditForm = (resource) => {
    setForm({
      title: resource.title || '',
      description: resource.description || '',
      resourceType: resource.resourceType || 'pdf',
      fileUrl: resource.fileUrl || '',
    })
    setResourceFile(null)
    setEditingId(resource.id)
    setShowForm(true)
  }

  const handleSave = async (e) => {
    e.preventDefault()
    if (!form.title.trim()) return
    try {
      setSaving(true)
      if (editingId) {
        const payload = {
          title: form.title,
          description: form.description,
          resourceType: form.resourceType,
          ...(form.resourceType === 'link' && { fileUrl: form.fileUrl }),
        }
        await api.put(`/api/facilitators/resources/${editingId}`, payload)
      } else if (form.resourceType === 'link') {
        await api.post('/api/facilitators/resources', {
          title: form.title,
          description: form.description,
          resourceType: form.resourceType,
          fileUrl: form.fileUrl,
        })
      } else {
        if (!resourceFile) return
        const formData = new FormData()
        formData.append('file', resourceFile)
        formData.append('title', form.title)
        formData.append('description', form.description)
        formData.append('resourceType', form.resourceType)
        await api.post('/api/facilitators/resources', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      }
      setShowForm(false)
      fetchResources()
    } catch (err) {
      console.error('Error saving resource:', err)
    } finally {
      setSaving(false)
    }
  }

  const handleToggleActive = async (resource) => {
    try {
      await api.put(`/api/facilitators/resources/${resource.id}`, { active: !resource.active })
      fetchResources()
    } catch (err) {
      console.error('Error toggling active:', err)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this resource? This cannot be undone.')) return
    try {
      await api.delete(`/api/facilitators/resources/${id}`)
      fetchResources()
    } catch (err) {
      console.error('Error deleting resource:', err)
    }
  }

  const formatFileSize = (bytes) => {
    if (!bytes) return null
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-cream to-primary-100 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-cream to-primary-100">
      <div className="container mx-auto px-4 py-12">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Resources</h1>
              <p className="text-gray-600">Share PDFs, videos, and links with your clients.</p>
            </div>
            <button
              onClick={openCreateForm}
              className="inline-flex items-center gap-2 px-5 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add Resource
            </button>
          </div>
        </motion.div>

        {resources.length === 0 ? (
          <Card className="text-center py-16">
            <p className="text-gray-500 text-lg mb-4">No resources yet.</p>
            <button
              onClick={openCreateForm}
              className="inline-flex items-center gap-2 px-5 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add Your First Resource
            </button>
          </Card>
        ) : (
          <div className="space-y-4">
            {resources.map((resource) => {
              const meta = TYPE_META[resource.resourceType] || TYPE_META.doc
              const TypeIcon = meta.icon
              return (
                <motion.div key={resource.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                  <Card className="p-5">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                      <div className="flex items-start gap-4 flex-1 min-w-0">
                        <div className={`p-2.5 rounded-xl flex-shrink-0 ${meta.color}`}>
                          <TypeIcon className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <h3 className="text-base font-bold text-gray-900">{resource.title}</h3>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${meta.color}`}>
                              {resource.resourceType?.toUpperCase()}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${resource.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                              {resource.active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          {resource.description && (
                            <p className="text-gray-600 text-sm mb-1 line-clamp-2">{resource.description}</p>
                          )}
                          {resource.fileSize && (
                            <p className="text-xs text-gray-400">{formatFileSize(resource.fileSize)}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => openEditForm(resource)}
                          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleActive(resource)}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                            resource.active
                              ? 'text-gray-600 bg-gray-100 hover:bg-gray-200'
                              : 'text-green-700 bg-green-100 hover:bg-green-200'
                          }`}
                        >
                          {resource.active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => handleDelete(resource.id)}
                          className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              )
            })}
          </div>
        )}

        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-0 md:p-4"
              onClick={(e) => { if (e.target === e.currentTarget) setShowForm(false) }}
            >
              <motion.div
                initial={{ y: '100%', opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: '100%', opacity: 0 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className="bg-white w-full md:max-w-lg md:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-y-auto"
              >
                <form onSubmit={handleSave} className="p-6 space-y-4">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-xl font-bold text-gray-900">
                      {editingId ? 'Edit Resource' : 'Add Resource'}
                    </h2>
                    <button type="button" onClick={() => setShowForm(false)} className="p-2 hover:bg-gray-100 rounded-full">
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={form.title}
                      onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="e.g., Breathing Guide PDF"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      rows={2}
                      value={form.description}
                      onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                      placeholder="Brief description..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Resource Type</label>
                    <select
                      value={form.resourceType}
                      onChange={(e) => setForm(prev => ({ ...prev, resourceType: e.target.value, fileUrl: '' }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      {TYPE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  {form.resourceType === 'link' ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        URL <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="url"
                        required
                        value={form.fileUrl}
                        onChange={(e) => setForm(prev => ({ ...prev, fileUrl: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="https://..."
                      />
                    </div>
                  ) : !editingId ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        File <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="file"
                        required
                        accept={
                          form.resourceType === 'pdf' ? '.pdf' :
                          form.resourceType === 'video' ? 'video/*' : '*'
                        }
                        onChange={(e) => setResourceFile(e.target.files[0] || null)}
                        className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                      />
                    </div>
                  ) : null}

                  <button
                    type="submit"
                    disabled={saving}
                    className="w-full py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : editingId ? 'Update Resource' : 'Add Resource'}
                  </button>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

export default FacilitatorResourceManage
